// Iter 248 — X5 Rollover (DC + HT) panel.
// Mounted inside AutopilotPage. Independent from the Parlay-mode card:
// NEVER changes the Parlay toggle or stake.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import HitTrackerBadge from "../components/HitTrackerBadge";
import { LegRow } from "../components/ParlayLegRow";

const STATE_BADGE = {
  planning: "bg-slate-700 text-slate-200",
  placing: "bg-amber-500/30 text-amber-200",
  placed: "bg-indigo-500/30 text-indigo-200",
  settled: "bg-emerald-600/40 text-emerald-100",
  skipped: "bg-slate-600/40 text-slate-200",
};

const fmtOdds = (v) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(2));
const fmtMoney = (v) => (v == null || isNaN(v) ? "—" : `$${Number(v).toFixed(2)}`);

export default function X5RolloverPanel() {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [dryOut, setDryOut] = useState(null);

  const auth = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/x5-rollover/status`, { headers: auth });
      setStatus(data);
      setDraft(data?.config || null);
    } catch (e) {
      toast.error("Failed to load X5 Rollover: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const saveConfig = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const payload = {
        starting_stake: Number(draft.starting_stake),
        max_stake: Number(draft.max_stake),
        kickoff_lead_min: Number(draft.kickoff_lead_min),
        min_combined_odds: Number(draft.min_combined_odds),
        max_combined_odds: Number(draft.max_combined_odds),
        max_legs: Number(draft.max_legs),
        min_legs: Number(draft.min_legs),
        pick_from: draft.pick_from || "saved_list",
      };
      await axios.post(`${API}/x5-rollover/config`, payload, { headers: auth });
      toast.success("X5 Rollover config saved");
      load();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const startCycle = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/x5-rollover/cycle/start`, {}, { headers: auth });
      toast.success("X5 cycle started");
      load();
    } catch (e) {
      toast.error("Start failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const stopCycle = async () => {
    if (!window.confirm("Stop the X5 Rollover cycle now? Any already-placed day keeps settling, but no new day will be auto-placed."))
      return;
    setRunning(true);
    try {
      await axios.post(`${API}/x5-rollover/cycle/stop`, {}, { headers: auth });
      toast.success("X5 cycle stopped");
      load();
    } catch (e) {
      toast.error("Stop failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const dryRun = async () => {
    setRunning(true);
    setDryOut(null);
    try {
      const { data } = await axios.post(`${API}/x5-rollover/dry-run`, {}, { headers: auth });
      setDryOut(data);
      toast.info("Dry-run: " + (data?.message || data?.stage || "done"));
    } catch (e) {
      toast.error("Dry-run failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const manualTick = async () => {
    if (!window.confirm(
      "Place TODAY'S X5 Rollover slip NOW on Betway with the current stake? Real money. Continue?"
    )) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/x5-rollover/tick`, {}, { headers: auth });
      toast.success("Tick: " + (data?.message || data?.stage || "fired"));
      load();
    } catch (e) {
      toast.error("Tick failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-slate-900 border-slate-800" data-testid="x5-rollover-card">
        <CardContent className="p-6 text-slate-400">Loading X5 Rollover…</CardContent>
      </Card>
    );
  }

  const cfg = status?.config || {};
  const cycle = status?.cycle;
  const td = status?.today_day;
  const recent = status?.recent_days || [];

  return (
    <Card className="bg-slate-900 border-emerald-500/30" data-testid="x5-rollover-card">
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-white text-lg">
            X5 Rollover (DC + HT) <span className="text-emerald-300">· isolated</span>
          </CardTitle>
          <div className="text-xs text-slate-500 mt-1 max-w-xl">
            One parlay a day from the Schedule-locked <span className="text-slate-300">x5</span>{" "}
            snapshot. Live combined odds must land inside [{cfg.min_combined_odds},{" "}
            {cfg.max_combined_odds}]. Rollover re-stakes Betway&apos;s actual return. Any
            losing leg ends the cycle. Independent of the Parlay toggle above.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            className={cycle ? "bg-emerald-600 text-white" : "bg-slate-700 text-slate-200"}
            data-testid="x5-cycle-state"
          >
            {cycle ? "CYCLE ACTIVE" : "NO CYCLE"}
          </Badge>
          <Badge
            className={cfg.enabled ? "bg-indigo-600 text-white" : "bg-slate-700 text-slate-200"}
            data-testid="x5-enabled-state"
          >
            {cfg.enabled ? "AUTO ON" : "AUTO OFF"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Starting stake" value={fmtMoney(cfg.starting_stake)} testid="x5-kpi-start" />
          <Kpi label="Next stake" value={fmtMoney(cfg.current_stake)} testid="x5-kpi-next" />
          <Kpi label="Max stake cap" value={fmtMoney(cfg.max_stake)} testid="x5-kpi-max" />
          <Kpi
            label="Cycle PnL"
            value={fmtMoney(cycle?.net_pnl ?? 0)}
            tone={(cycle?.net_pnl ?? 0) >= 0 ? "pos" : "neg"}
            testid="x5-kpi-pnl"
          />
        </div>

        {/* Pick from (saved list vs live schedule) */}
        <div className="bg-slate-950/40 border border-slate-800 rounded p-3" data-testid="x5-pick-from-row">
          <Label className="text-slate-400 text-xs mb-2 block uppercase tracking-wider">
            Pick from
          </Label>
          <div className="flex flex-wrap gap-2">
            {[
              { k: "saved_list", lbl: "Saved list", hint: "The Schedule page's saved X5 Odds (DC + HT) list" },
              { k: "live_schedule", lbl: "Live schedule", hint: "Recompute X5 Odds from today's live schedule; freeze the slip; no substitution" },
            ].map((o) => {
              const active = (draft?.pick_from || "saved_list") === o.k;
              return (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => setDraft({ ...draft, pick_from: o.k })}
                  data-testid={`x5-pick-from-${o.k}`}
                  className={
                    "px-3 py-1.5 rounded text-xs font-medium border transition-colors " +
                    (active
                      ? "border-indigo-400 bg-indigo-500/20 text-indigo-200"
                      : "border-slate-700 bg-slate-900 text-slate-300 hover:border-slate-500")
                  }
                  title={o.hint}
                >
                  {o.lbl}
                </button>
              );
            })}
            <div className="text-[11px] text-slate-500 self-center ml-1">
              {(draft?.pick_from || "saved_list") === "live_schedule"
                ? "Freezes legs from live schedule using the same X5 Odds (DC + HT) rules. Failed legs are dropped (no substitution); day skips if too few remain."
                : "Uses the list the Schedule page saved for the day."}
            </div>
          </div>
        </div>

        {/* Config form */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <NumField
            label="Starting stake ($)"
            value={draft?.starting_stake}
            step={0.5}
            onChange={(v) => setDraft({ ...draft, starting_stake: v })}
            testid="x5-cfg-starting"
          />
          <NumField
            label="Max stake cap ($)"
            value={draft?.max_stake}
            step={1}
            onChange={(v) => setDraft({ ...draft, max_stake: v })}
            testid="x5-cfg-maxstake"
          />
          <NumField
            label="Min live odds"
            value={draft?.min_combined_odds}
            step={0.05}
            onChange={(v) => setDraft({ ...draft, min_combined_odds: v })}
            testid="x5-cfg-min-odds"
          />
          <NumField
            label="Max live odds"
            value={draft?.max_combined_odds}
            step={0.05}
            onChange={(v) => setDraft({ ...draft, max_combined_odds: v })}
            testid="x5-cfg-max-odds"
          />
          <NumField
            label="Kick-off lead (min)"
            value={draft?.kickoff_lead_min}
            step={5}
            onChange={(v) => setDraft({ ...draft, kickoff_lead_min: v })}
            testid="x5-cfg-lead"
          />
          <NumField
            label="Max legs"
            value={draft?.max_legs}
            step={1}
            min={2}
            max={5}
            onChange={(v) => setDraft({ ...draft, max_legs: v })}
            testid="x5-cfg-maxlegs"
          />
          <NumField
            label="Min legs"
            value={draft?.min_legs}
            step={1}
            min={2}
            max={5}
            onChange={(v) => setDraft({ ...draft, min_legs: v })}
            testid="x5-cfg-minlegs"
          />
          <div className="flex items-end">
            <Button
              size="sm"
              onClick={saveConfig}
              disabled={saving}
              className="w-full"
              data-testid="x5-save-btn"
            >
              {saving ? "Saving…" : "Save config"}
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={dryRun}
            disabled={running}
            data-testid="x5-dry-run-btn"
          >
            Dry run (no Betway)
          </Button>
          <Button
            size="sm"
            onClick={startCycle}
            disabled={running || !!cycle}
            data-testid="x5-start-cycle-btn"
          >
            Start cycle
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={stopCycle}
            disabled={running || !cycle}
            data-testid="x5-stop-cycle-btn"
          >
            Stop cycle
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={manualTick}
            disabled={running || !cfg.enabled || !cycle}
            data-testid="x5-manual-tick-btn"
          >
            Fire today&apos;s slip now
          </Button>
        </div>

        {/* Today */}
        <div className="bg-slate-950/50 border border-slate-800 rounded p-3" data-testid="x5-today-panel">
          <div className="text-slate-400 text-xs uppercase tracking-wider mb-2">
            Today ({status?.today})
          </div>
          {td ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={STATE_BADGE[td.state] || "bg-slate-700 text-slate-200"}>
                  {td.state}
                </Badge>
                {td.slip_tracker ? <HitTrackerBadge tracker={td.slip_tracker} /> : null}
                <span className="text-slate-300">
                  stake {fmtMoney(td.stake)} · live odds {fmtOdds(td.combined_live_odds)}
                </span>
              </div>
              {td.message ? <div className="text-slate-400 text-xs">{td.message}</div> : null}
              {td.legs?.length ? (
                <ul className="divide-y divide-slate-800/70" data-testid="x5-today-legs">
                  {td.legs.map((l, i) => (
                    <LegRow key={i} leg={l} />
                  ))}
                </ul>
              ) : null}
              {td.state === "settled" ? (
                <div className="text-emerald-300 text-sm pt-1">
                  Return: {fmtMoney(td.actual_return)} · outcome {td.outcome}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-slate-500 text-sm">
              Nothing fired for today yet.
            </div>
          )}
        </div>

        {/* Dry run plan preview */}
        {dryOut ? (
          <div
            className="bg-slate-950/50 border border-indigo-500/30 rounded p-3 text-xs text-slate-300 overflow-x-auto"
            data-testid="x5-dry-run-out"
          >
            <div className="text-indigo-300 mb-1">
              Dry-run · {dryOut.stage}
              {dryOut.pick_from ? (
                <span className="ml-2 text-slate-400">
                  (pick from: <span className="text-indigo-200">{dryOut.pick_from}</span>)
                </span>
              ) : null}
            </div>
            <div className="text-slate-400 mb-2">{dryOut.message}</div>
            {dryOut.frozen_legs?.length ? (
              <div className="mb-2" data-testid="x5-dry-run-frozen">
                <div className="text-slate-300 mb-1">
                  Frozen slip · {dryOut.frozen_legs.length} leg(s):
                </div>
                <ul className="list-disc pl-5">
                  {dryOut.frozen_legs.map((p, i) => (
                    <li key={i}>
                      {p.home} vs {p.away} · {p.market_type} / {p.prediction} · conf{" "}
                      {p.confidence?.toFixed?.(1)}%
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {dryOut.plan?.subsets?.length ? (
              <div>
                First subset would try {dryOut.plan.subsets[0].length} leg(s) at{" "}
                {fmtMoney(dryOut.plan.stake)} inside [{dryOut.plan.min_odds},{" "}
                {dryOut.plan.max_odds}]:
                <ul className="list-disc pl-5 mt-1">
                  {dryOut.plan.subsets[0].map((p, i) => (
                    <li key={i}>
                      {p.home} vs {p.away} · {p.market} / {p.pick} · conf {p.conf?.toFixed?.(1)}%
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Recent days */}
        <div data-testid="x5-recent-days">
          <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">
            Recent days ({recent.length})
          </div>
          <table className="w-full text-xs">
            <thead className="text-slate-500 text-left">
              <tr className="border-b border-slate-800">
                <th className="p-1.5">Date</th>
                <th>State</th>
                <th>Tracker</th>
                <th>Legs</th>
                <th>Stake</th>
                <th>Live odds</th>
                <th>Return</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((d) => (
                <tr key={d.day_id} className="border-b border-slate-800/50">
                  <td className="p-1.5 text-slate-400">{d.date}</td>
                  <td>
                    <Badge className={STATE_BADGE[d.state] || "bg-slate-700 text-slate-200"}>
                      {d.state}
                    </Badge>
                  </td>
                  <td>
                    {d.slip_tracker ? <HitTrackerBadge tracker={d.slip_tracker} /> : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="text-slate-300">{d.legs?.length ?? 0}</td>
                  <td className="text-amber-300">{fmtMoney(d.stake)}</td>
                  <td className="text-slate-300">{fmtOdds(d.combined_live_odds)}</td>
                  <td className="text-emerald-300">{fmtMoney(d.actual_return)}</td>
                  <td className="text-slate-400">{d.outcome || "—"}</td>
                </tr>
              ))}
              {!recent.length ? (
                <tr>
                  <td colSpan={8} className="p-4 text-center text-slate-500">
                    No X5 Rollover days yet. Press <span className="text-emerald-300">Start cycle</span>{" "}
                    then save your stake caps to begin.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

const Kpi = ({ label, value, tone, testid }) => (
  <div className="bg-slate-950/50 border border-slate-800 rounded p-3" data-testid={testid}>
    <div className="text-slate-500 text-[10px] uppercase tracking-wider">{label}</div>
    <div
      className={
        "text-xl font-bold " +
        (tone === "pos" ? "text-emerald-300" : tone === "neg" ? "text-rose-300" : "text-white")
      }
    >
      {value}
    </div>
  </div>
);

const NumField = ({ label, value, step = 1, min, max, onChange, testid }) => (
  <div>
    <Label className="text-slate-400 text-xs mb-1 block">{label}</Label>
    <Input
      type="number"
      step={step}
      min={min}
      max={max}
      value={value ?? ""}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="bg-slate-950 border-slate-700 text-white"
      data-testid={testid}
    />
  </div>
);
